// @ts-nocheck
import React, { useState } from 'react';
import { Info, Lock, ChevronDown } from 'lucide-react';
import { sx } from '../sx';

const ACCENT = '#0052CD';
const INK = '#1B2540';
const INK_2 = '#3B455E';
const INK_3 = '#5B6478';
const LINE = '#E3E7EF';
const CAPS = `margin:0;font-size:12px;font-weight:600;letter-spacing:0.08em;text-transform:uppercase;color:${INK_3}`;
const CARD = `background:#fff;border:1px solid ${LINE};border-radius:16px`;
const INPUT = `box-sizing:border-box;padding:0 12px;border-radius:8px;color:${INK};font-family:inherit;font-size:14px;font-variant-numeric:tabular-nums;outline:none`;
const TABLE_WRAP = `overflow-x:auto;border:1px solid ${LINE};border-radius:12px`;
const TH = `padding:12px 16px;font-size:12px;font-weight:600;color:${INK_3};text-align:left;white-space:nowrap`;
const TD = `padding:10px 16px;border-top:1px solid ${LINE};vertical-align:middle`;
const LINK_BTN = `background:none;border:0;padding:0;font-family:inherit;font-size:14px;font-weight:500;color:${ACCENT};cursor:pointer;min-height:44px`;
const HELP_PARTS = [['what', 'What it is'], ['why', 'Why it matters'], ['how', 'How it is set'], ['when', 'When to change it']];
const STATUS = {
  user: { text: 'Set by user', fg: ACCENT },
  system: { text: 'System default', fg: INK_3 },
  needs: { text: 'Needs a decision', fg: '#B42318' }
};

function InfoButton({ id, label, open, onToggle, help }) {
  if (!help) return null;
  const on = open === id;
  return (
    <button
      type="button"
      aria-label={'More about ' + label}
      aria-expanded={on}
      onClick={() => onToggle(on ? null : id)}
      style={sx(`flex:none;width:32px;height:32px;border-radius:999px;border:1px solid ${on ? ACCENT : '#D6E0F7'};background:${on ? '#F0F4FD' : '#fff'};color:${ACCENT};display:inline-flex;align-items:center;justify-content:center;cursor:pointer;padding:0`)}
    >
      <Info size={18} strokeWidth={2} aria-hidden="true" />
    </button>
  );
}

function HelpPanel({ id, open, help }) {
  if (!help || open !== id) return null;
  return (
    <div role="region" aria-label="Help" data-testid={'guard-help-' + id} style={sx(`display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px 24px;margin-top:12px;padding:16px 18px;border:1px solid #D6E0F7;border-radius:10px;background:#F0F4FD`)}>
      {HELP_PARTS.map(([k, title]) => (
        <div key={k}>
          <p style={sx(`margin:0;font-size:11px;font-weight:600;letter-spacing:0.08em;text-transform:uppercase;color:${ACCENT}`)}>{title}</p>
          <p style={sx(`margin:2px 0 0;font-size:14px;line-height:1.6;color:${INK}`)}>{help[k]}</p>
        </div>
      ))}
    </div>
  );
}

function GuardInput({ item, width, height = 44, suffix }) {
  const [text, setText] = useState(item.value);
  const [bad, setBad] = useState(false);
  const commit = () => {
    if (text === item.value) return setBad(false);
    setBad(!item.onCommit(text));
  };
  const red = bad || item.status === 'needs';
  return (
    <span style={sx(`display:inline-flex;align-items:center;gap:6px;${width ? '' : 'flex:1 1 160px;min-width:0'}`)}>
      <input
        className="gmFocus gmGuardInput"
        aria-label={item.label}
        aria-invalid={bad || undefined}
        value={text}
        disabled={item.disabled}
        placeholder={item.placeholder}
        onChange={e => { setText(e.target.value); setBad(false); }}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setText(item.value); setBad(false); } }}
        style={sx(`${INPUT};height:${height}px;width:${width ? width + 'px' : '100%'};border:1px solid ${red ? '#E5484D' : '#C9D0DD'};background:${item.disabled ? '#F5F6FA' : '#fff'}`)}
      />
      {suffix ? <span style={sx(`color:${INK_3}`)}>{suffix}</span> : null}
    </span>
  );
}

function GuardMonthSelect({ item }) {
  const { value, options } = item.months;
  return (
    <span style={sx(`display:inline-flex;align-items:center;flex:1 1 160px;min-width:0`)}>
      <select
        className="gmFocus gmGuardInput"
        aria-label={item.label}
        value={value === null ? '' : String(value)}
        disabled={item.disabled}
        onChange={e => item.onCommit(e.target.value)}
        style={sx(`${INPUT};height:44px;width:100%;border:1px solid #C9D0DD;background:${item.disabled ? '#F5F6FA' : '#fff'};cursor:${item.disabled ? 'default' : 'pointer'}`)}
      >
        {options.map(o => <option key={o.value} value={String(o.value)}>{o.label}</option>)}
      </select>
    </span>
  );
}

function StatusNote({ status, note }) {
  if (!note) return null;
  const s = STATUS[status];
  const tagged = s && note.indexOf(s.text) === 0;
  return (
    <p style={sx(`margin:8px 0 0;font-size:13px;line-height:1.5;color:${status === 'needs' ? '#B42318' : INK_3}`)}>
      {tagged ? <span style={sx(`font-weight:600;color:${s.fg}`)}>{s.text}</span> : null}
      {tagged ? note.slice(s.text.length) : note}
    </p>
  );
}

function ClearButton({ item }) {
  if (item.status !== 'user' || item.disabled) return null;
  return (
    <button type="button" className="gmHover" onClick={item.onClear} data-hover="border-color:#00B3FF" style={sx(`flex:none;height:44px;padding:0 14px;border-radius:8px;border:1px solid ${LINE};background:#F5F6FA;color:${ACCENT};font-family:inherit;font-size:13px;font-weight:500;cursor:pointer`)}>
      Clear
    </button>
  );
}

function BasicRow({ item, open, onToggle }) {
  return (
    <div data-testid={'guard-param-' + item.id} style={sx(`padding:24px 0;border-top:1px solid ${LINE}`)}>
      <div style={sx(`display:flex;flex-wrap:wrap;gap:16px 32px;align-items:flex-start`)}>
        <div style={sx(`flex:1 1 460px;min-width:0`)}>
          <div style={sx(`display:flex;align-items:center;gap:10px`)}>
            <span style={sx(`font-size:16px;font-weight:600;color:${INK}`)}>{item.label}</span>
            <InfoButton id={item.id} label={item.label} open={open} onToggle={onToggle} help={item.help} />
          </div>
          {item.desc ? <p style={sx(`margin:6px 0 0;font-size:14px;line-height:1.6;color:${INK_2}`)}>{item.desc}</p> : null}
          <StatusNote status={item.status} note={item.note} />
        </div>
        <div style={sx(`flex:0 1 320px;display:flex;flex-wrap:wrap;align-items:center;gap:8px 12px`)}>
          {item.months ? <GuardMonthSelect item={item} /> : <GuardInput key={item.key} item={item} />}
          <ClearButton item={item} />
        </div>
      </div>
      <HelpPanel id={item.id} open={open} help={item.help} />
    </div>
  );
}

function TrendRow({ trend, open, onToggle }) {
  const [editing, setEditing] = useState(false);
  return (
    <div data-testid="guard-param-trendLimits" style={sx(`padding:24px 0;border-top:1px solid ${LINE}`)}>
      <div style={sx(`display:flex;align-items:center;gap:10px`)}>
        <span id="gm-trend-label" style={sx(`font-size:16px;font-weight:600;color:${INK}`)}>{trend.label}</span>
        <InfoButton id="trendLimits" label={trend.label} open={open} onToggle={onToggle} help={trend.help} />
      </div>
      <p style={sx(`margin:6px 0 0;font-size:14px;line-height:1.6;color:${INK_2};max-width:760px`)}>{trend.desc}</p>
      <HelpPanel id="trendLimits" open={open} help={trend.help} />
      {trend.used ? (
        <div style={sx(`margin-top:12px;display:flex;flex-wrap:wrap;align-items:center;gap:8px 20px`)}>
          <p style={sx(`margin:0;font-size:14px;color:${INK}`)}>
            <span style={sx(`font-weight:600`)}>{trend.lead}</span> {trend.summary} ·{' '}
            <span style={sx(`color:${trend.sourceLabel === 'System default' ? INK_3 : ACCENT}`)}>{trend.sourceLabel}</span>
          </p>
          <button type="button" aria-expanded={editing} onClick={() => setEditing(!editing)} style={sx(LINK_BTN)}>
            {editing ? 'Hide age groups' : 'Edit per age group'}
          </button>
        </div>
      ) : (
        <p data-testid="guard-trend-unused" style={sx(`margin:12px 0 0;font-size:14px;color:${INK_3}`)}>{trend.unusedNote}</p>
      )}
      {trend.used && editing ? (
        <div style={sx(`margin-top:12px;${TABLE_WRAP}`)}>
          <table data-testid="guard-trend-table" aria-labelledby="gm-trend-label" style={sx(`width:100%;min-width:640px;border-collapse:collapse;font-size:14px`)}>
            <thead>
              <tr style={sx(`background:#F5F6FA`)}>
                <th scope="col" style={sx(TH)}>Age group</th>
                <th scope="col" style={sx(TH)}>Months since first order</th>
                <th scope="col" style={sx(TH)}>Largest fall a year</th>
                <th scope="col" style={sx(TH)}>Largest rise a year</th>
              </tr>
            </thead>
            <tbody>
              {trend.groups.map(g => (
                <tr key={g.name}>
                  <td style={sx(`${TD};font-weight:500;color:${INK}`)}>{g.name}</td>
                  <td style={sx(`${TD};color:${INK_2}`)}>{g.months}</td>
                  <td style={sx(`${TD};padding:8px 16px`)}><GuardInput key={g.fall.key} item={g.fall} width={150} height={40} suffix="%" /></td>
                  <td style={sx(`${TD};padding:8px 16px`)}><GuardInput key={g.rise.key} item={g.rise} width={150} height={40} suffix="%" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}

function ChoiceRow({ item, open, onToggle }) {
  return (
    <div data-testid={'guard-param-' + item.id} style={sx(`padding:24px 0;border-top:1px solid ${LINE}`)}>
      <div style={sx(`display:flex;flex-wrap:wrap;gap:16px 32px;align-items:flex-start`)}>
        <div style={sx(`flex:1 1 460px;min-width:0`)}>
          <div style={sx(`display:flex;align-items:center;gap:10px`)}>
            <span id={'gm-' + item.id + '-label'} style={sx(`font-size:16px;font-weight:600;color:${INK}`)}>{item.label}</span>
            <InfoButton id={item.id} label={item.label} open={open} onToggle={onToggle} help={item.help} />
          </div>
          {item.desc ? <p style={sx(`margin:6px 0 0;font-size:14px;line-height:1.6;color:${INK_2}`)}>{item.desc}</p> : null}
          <StatusNote status={item.status} note={item.note} />
        </div>
        <div role="radiogroup" aria-labelledby={'gm-' + item.id + '-label'} style={sx(`flex:0 1 320px;display:flex;flex-wrap:wrap;gap:8px`)}>
          {item.options.map(o => (
            <button
              key={o.id}
              type="button"
              role="radio"
              aria-checked={o.on}
              disabled={item.disabled}
              onClick={() => item.onPick(Number(o.id))}
              style={sx(`height:40px;padding:0 14px;border-radius:999px;border:1px solid ${o.on ? ACCENT : '#C9D0DD'};background:${o.on ? ACCENT : '#fff'};color:${o.on ? '#fff' : INK_2};font-family:inherit;font-size:13px;font-weight:500;cursor:${item.disabled ? 'not-allowed' : 'pointer'};opacity:${item.disabled && !o.on ? 0.6 : 1}`)}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>
      <HelpPanel id={item.id} open={open} help={item.help} />
    </div>
  );
}

function SignalRow({ signal, open, onToggle }) {
  return (
    <div data-testid="guard-retention-signal" style={sx(`padding:0 0 24px`)}>
      <div style={sx(`display:flex;align-items:center;gap:10px`)}>
        <span id="gm-signal-label" style={sx(`font-size:14px;font-weight:600;color:${INK_2}`)}>{signal.label}</span>
        <InfoButton id="retentionSignal" label={signal.label} open={open} onToggle={onToggle} help={signal.help} />
      </div>
      <p style={sx(`margin:4px 0 0;font-size:13px;line-height:1.5;color:${INK_3};max-width:760px`)}>{signal.desc}</p>
      <HelpPanel id="retentionSignal" open={open} help={signal.help} />
      <div style={sx(`margin-top:12px;${TABLE_WRAP}`)}>
        <table aria-labelledby="gm-signal-label" style={sx(`width:100%;min-width:560px;border-collapse:collapse;font-size:14px`)}>
          <thead>
            <tr style={sx(`background:#F5F6FA`)}>
              <th scope="col" style={sx(TH)}>Age group</th>
              <th scope="col" style={sx(TH)}>Months since first order</th>
              <th scope="col" style={sx(TH)}>Change on a year earlier</th>
              <th scope="col" style={sx(TH)}>Cohort pairs</th>
            </tr>
          </thead>
          <tbody>
            {signal.groups.map(g => (
              <tr key={g.name}>
                <td style={sx(`${TD};font-weight:500;color:${INK}`)}>{g.name}</td>
                <td style={sx(`${TD};color:${INK_2}`)}>{g.months}</td>
                <td style={sx(`${TD};color:${INK_3};font-variant-numeric:tabular-nums`)}>{g.change}</td>
                <td style={sx(`${TD};color:${INK_3};font-variant-numeric:tabular-nums`)}>{g.pairs}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SeasonRow({ season, open, onToggle }) {
  return (
    <div data-testid="guard-param-monthFactors" style={sx(`padding:24px 0;border-top:1px solid ${LINE}`)}>
      <div style={sx(`display:flex;align-items:center;gap:10px`)}>
        <span id="gm-season-label" style={sx(`font-size:16px;font-weight:600;color:${INK}`)}>{season.label}</span>
        <InfoButton id="monthFactors" label={season.label} open={open} onToggle={onToggle} help={season.help} />
        <span style={sx(`margin-left:auto;font-size:13px;color:${INK_3}`)}>{season.count}</span>
      </div>
      <p style={sx(`margin:6px 0 0;font-size:14px;line-height:1.6;color:${INK_2};max-width:760px`)}>{season.desc}</p>
      <HelpPanel id="monthFactors" open={open} help={season.help} />
      <div style={sx(`margin-top:12px;${TABLE_WRAP}`)}>
        <table data-testid="guard-factors" aria-labelledby="gm-season-label" style={sx(`width:100%;min-width:640px;border-collapse:collapse;font-size:14px`)}>
          <thead>
            <tr style={sx(`background:#F5F6FA`)}>
              {season.factors.map(f => <th key={f.name} scope="col" style={sx(TH)}>{f.name}</th>)}
            </tr>
          </thead>
          <tbody>
            <tr>
              {season.factors.map(f => <td key={f.name} style={sx(`${TD};color:${INK_3};font-variant-numeric:tabular-nums`)}>{f.text}</td>)}
            </tr>
          </tbody>
        </table>
      </div>
      {season.note ? <p data-testid="guard-factors-note" style={sx(`margin:8px 0 0;font-size:13px;color:${INK_3}`)}>{season.note}</p> : null}
    </div>
  );
}

function ItemRow({ item, open, onToggle, top }) {
  return (
    <div data-testid={'guard-param-' + item.id} style={sx(top)}>
      <div style={sx(`display:flex;flex-wrap:wrap;gap:8px 24px;align-items:center`)}>
        <div style={sx(`flex:1 1 420px;min-width:0`)}>
          <div style={sx(`display:flex;align-items:center;gap:10px`)}>
            <span style={sx(`font-size:14px;font-weight:500;color:${INK}`)}>{item.label}</span>
            <InfoButton id={item.id} label={item.label} open={open} onToggle={onToggle} help={item.help} />
          </div>
          {item.readOnly
            ? (item.reason ? <p style={sx(`margin:4px 0 0;font-size:13px;color:${INK_3}`)}>{item.reason}</p> : null)
            : <StatusNote status={item.status} note={item.note} />}
        </div>
        {item.readOnly ? (
          <output style={sx(`flex:0 1 220px;min-width:140px;font-size:14px;font-weight:500;color:${item.status === 'needs' ? '#B42318' : INK};font-variant-numeric:tabular-nums`)}>
            {item.shown} <span style={sx(`font-weight:400;color:${INK_3}`)}>· read only</span>
          </output>
        ) : (
          <span style={sx(`flex:0 1 300px;min-width:140px;display:flex;align-items:center;gap:8px`)}>
            <GuardInput key={item.key} item={item} height={40} />
            <ClearButton item={item} />
          </span>
        )}
      </div>
      <HelpPanel id={item.id} open={open} help={item.help} />
    </div>
  );
}

function Section({ s, open, onToggle }) {
  const helpId = 'section-' + s.id;
  return (
    <div data-testid={'guard-card-' + s.id} style={sx(`border:1px solid ${LINE};border-radius:12px;padding:20px`)}>
      <div style={sx(`display:flex;flex-wrap:wrap;align-items:center;gap:8px 12px`)}>
        <span style={sx(`font-size:15px;font-weight:600;color:${INK}`)}>{s.title}</span>
        <InfoButton id={helpId} label={s.title} open={open} onToggle={onToggle} help={s.help} />
        <span style={sx(`margin-left:auto;font-size:13px;color:${INK_3}`)}>{s.count}</span>
      </div>
      <p style={sx(`margin:4px 0 0;font-size:14px;line-height:1.6;color:${INK_2}`)}>{s.desc}</p>
      <HelpPanel id={helpId} open={open} help={s.help} />
      {s.items.map(it => <ItemRow key={it.id} item={it} open={open} onToggle={onToggle} top={`margin-top:14px;padding-top:14px;border-top:1px solid #EEF1F6`} />)}
      {s.caps ? (
        <>
          <div style={sx(`margin-top:14px;${TABLE_WRAP}`)}>
            <table data-testid="guard-caps" style={sx(`width:100%;min-width:560px;border-collapse:collapse;font-size:14px`)}>
              <thead>
                <tr style={sx(`background:#F5F6FA`)}>
                  <th scope="col" style={sx(TH)}>Age group</th>
                  <th scope="col" style={sx(TH)}>Months since first order</th>
                  {s.headroom ? <th scope="col" style={sx(TH)}>Best real rate</th> : null}
                  {s.headroom ? <th scope="col" style={sx(TH)}>Headroom</th> : null}
                  <th scope="col" style={sx(TH)}>
                    <span style={sx(`display:inline-flex;align-items:center;gap:8px`)}>
                      Safety ceiling · read only
                      <InfoButton id="rateCap" label="Safety ceiling" open={open} onToggle={onToggle} help={s.capsHelp} />
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {s.caps.map(c => (
                  <tr key={c.name}>
                    <td style={sx(`${TD};font-weight:500;color:${INK}`)}>{c.name}</td>
                    <td style={sx(`${TD};color:${INK_2}`)}>{c.months}</td>
                    {s.headroom ? <td style={sx(`${TD};color:${INK_3};font-variant-numeric:tabular-nums`)}>{c.best}</td> : null}
                    {s.headroom ? <td style={sx(`${TD};color:${INK_3};font-variant-numeric:tabular-nums`)}>{c.headroom}</td> : null}
                    <td style={sx(`${TD};color:${INK_3};font-variant-numeric:tabular-nums`)}>{c.text}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <HelpPanel id="rateCap" open={open} help={s.capsHelp} />
        </>
      ) : null}
    </div>
  );
}

function FoldHeader({ open, onClick, title, subtitle, name, icon, titleStyle }) {
  return (
    <button
      type="button"
      aria-expanded={open}
      aria-label={(open ? 'Hide ' : 'Show ') + name}
      onClick={onClick}
      style={sx(`flex:1 1 auto;min-width:0;display:flex;align-items:center;gap:16px;padding:20px 0;background:none;border:0;text-align:left;font-family:inherit;color:inherit;cursor:pointer`)}
    >
      {icon}
      <span style={sx(`flex:1 1 auto;min-width:0`)}>
        <span style={sx(`display:block;${titleStyle}`)}>{title}</span>
        <span style={sx(`display:block;margin-top:4px;font-size:13px;color:${INK_3}`)}>{subtitle}</span>
      </span>
      <span style={sx(`font-size:14px;font-weight:500;color:${ACCENT}`)}>{open ? 'Hide' : 'Show'}</span>
      <ChevronDown size={20} color={ACCENT} aria-hidden="true" style={{ transform: open ? 'rotate(180deg)' : 'none' }} />
    </button>
  );
}

export default function CohortGuardsSettings({ view }) {
  const [open, setOpen] = useState(null);
  const [advanced, setAdvanced] = useState(false);
  const [admin, setAdmin] = useState(false);
  const b = view.basic;
  return (
    <div style={sx(`display:flex;flex-direction:column;gap:24px;color:${INK}`)}>
      <p style={sx(`margin:0;font-size:14px;line-height:1.6;color:${INK_3};max-width:720px`)}>{view.intro}</p>

      <div style={sx(`${CARD};padding:18px 24px`)}>
        <div style={sx(`display:flex;flex-wrap:wrap;align-items:center;gap:12px 16px`)}>
          <label htmlFor="gm-guard-scope" style={sx(CAPS)}>Channel and cohort model</label>
          <select id="gm-guard-scope" aria-label="Guardrail scope" value={view.scope} onChange={view.onScope} style={sx(`${INPUT};height:44px;min-width:280px;border:1px solid #C9D0DD;background:#fff`)}>
            {view.scopeOpts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <InfoButton id="channel" label="Channel and cohort model" open={open} onToggle={setOpen} help={view.help.channel} />
        </div>
        <HelpPanel id="channel" open={open} help={view.help.channel} />
      </div>

      <section data-testid="guard-basic" style={sx(`${CARD};padding:28px 28px 8px`)}>
        <div style={sx(`display:flex;align-items:center;gap:10px`)}>
          <h2 style={sx(`margin:0;font-size:20px;font-weight:600`)}>Basic settings</h2>
          <InfoButton id="basic" label="Basic settings" open={open} onToggle={setOpen} help={b.help} />
        </div>
        <p style={sx(`margin:6px 0 0;font-size:14px;color:${INK_3}`)}>{b.desc}</p>
        <HelpPanel id="basic" open={open} help={b.help} />
        <div style={sx(`height:20px`)}></div>
        <BasicRow item={b.cutoff} open={open} onToggle={setOpen} />
        {b.retention ? (
          <>
            <BasicRow item={b.retention.change} open={open} onToggle={setOpen} />
            <SignalRow signal={b.retention.signal} open={open} onToggle={setOpen} />
            <BasicRow item={b.retention.start} open={open} onToggle={setOpen} />
          </>
        ) : null}
        {b.trend ? <TrendRow trend={b.trend} open={open} onToggle={setOpen} /> : null}
        {b.window ? <ChoiceRow item={b.window} open={open} onToggle={setOpen} /> : null}
        {b.years ? <BasicRow item={b.years} open={open} onToggle={setOpen} /> : null}
        {b.season ? <SeasonRow season={b.season} open={open} onToggle={setOpen} /> : null}
      </section>

      <section data-testid="guard-advanced" style={sx(`${CARD};padding:8px 28px`)}>
        <div style={sx(`display:flex;align-items:center;gap:12px`)}>
          <FoldHeader open={advanced} onClick={() => setAdvanced(!advanced)} title="Advanced settings" subtitle={view.summary} name="advanced settings" titleStyle="font-size:18px;font-weight:600" />
          <InfoButton id="advanced" label="Advanced settings" open={open} onToggle={setOpen} help={view.help.advanced} />
        </div>
        <HelpPanel id="advanced" open={open} help={view.help.advanced} />
        {advanced ? (
          <div style={sx(`display:flex;flex-direction:column;gap:16px;padding:12px 0 28px`)}>
            {view.sections.map(s => <Section key={s.id} s={s} open={open} onToggle={setOpen} />)}

            <div data-testid="guard-always" style={sx(`border:1px solid ${LINE};border-radius:12px;padding:16px 20px;background:#F5F6FA`)}>
              <div style={sx(`display:flex;flex-wrap:wrap;align-items:center;gap:8px 12px`)}>
                <p style={sx(`margin:0;font-size:14px;color:${INK_2}`)}>
                  <span style={sx(`font-weight:600;color:${INK}`)}>Always on:</span> {view.alwaysOn}
                </p>
                <InfoButton id="always" label="the rules that are always on" open={open} onToggle={setOpen} help={view.help.always} />
              </div>
              <HelpPanel id="always" open={open} help={view.help.always} />
            </div>

            <div style={sx(`border:1px solid ${LINE};border-radius:12px;padding:20px`)}>
              <div style={sx(`display:flex;align-items:center;gap:10px`)}>
                <span style={sx(`font-size:15px;font-weight:600;color:${INK}`)}>Where these settings apply</span>
                <InfoButton id="where" label="Where these settings apply" open={open} onToggle={setOpen} help={view.help.where} />
              </div>
              <HelpPanel id="where" open={open} help={view.help.where} />
              <div style={sx(`margin-top:12px;overflow-x:auto`)}>
                <table data-testid="guard-where" style={sx(`width:100%;min-width:560px;border-collapse:collapse;font-size:14px`)}>
                  <thead>
                    <tr style={sx(`background:#F5F6FA`)}>
                      <th scope="col" style={sx(`${TH};padding:10px 12px`)}>Channel and cohort model</th>
                      <th scope="col" style={sx(`${TH};padding:10px 12px`)}>Bundle before</th>
                      <th scope="col" style={sx(`${TH};padding:10px 12px`)}>Values set by a user</th>
                      <th scope="col" style={sx(`${TH};padding:10px 12px`)}>Red cells</th>
                    </tr>
                  </thead>
                  <tbody>
                    {view.where.map(r => (
                      <tr key={r.key} aria-selected={r.selected} style={sx(`background:${r.selected ? '#F0F4FD' : 'transparent'}`)}>
                        <td style={sx(`${TD};padding:10px 12px`)}>
                          <button type="button" onClick={r.onClick} style={sx(`background:none;border:0;padding:0;font-family:inherit;font-size:14px;color:${ACCENT};text-decoration:underline;cursor:pointer;text-align:left;font-weight:${r.selected ? 600 : 400}`)}>{r.label}</button>
                        </td>
                        <td style={sx(`${TD};padding:10px 12px;font-weight:${r.bundleUser ? 600 : 400};color:${INK}`)}>{r.bundleBefore}</td>
                        <td title={r.userTitle || undefined} style={sx(`${TD};padding:10px 12px;color:${INK};font-variant-numeric:tabular-nums`)}>{r.userCount}</td>
                        <td style={sx(`${TD};padding:10px 12px;color:${r.red ? '#B42318' : INK};font-variant-numeric:tabular-nums`)}>{r.red}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : null}
      </section>

      <section data-testid="guard-admin" style={sx(`background:#fff;border:1px dashed #C9D0DD;border-radius:16px;padding:8px 28px`)}>
        <div style={sx(`display:flex;align-items:center;gap:12px`)}>
          <FoldHeader
            open={admin}
            onClick={() => setAdmin(!admin)}
            title="How the system sets defaults"
            subtitle="Applies to every channel and cohort model in this version"
            name="how the system sets defaults"
            titleStyle={`font-size:15px;font-weight:600;color:${INK_2}`}
            icon={<Lock size={18} color={INK_3} aria-hidden="true" />}
          />
          <InfoButton id="admin" label="How the system sets defaults" open={open} onToggle={setOpen} help={view.help.admin} />
        </div>
        <HelpPanel id="admin" open={open} help={view.help.admin} />
        {admin ? (
          <div style={sx(`padding:8px 0 20px`)}>
            {view.constants.map(k => <ItemRow key={k.id} item={k} open={open} onToggle={setOpen} top={`padding:12px 0;border-top:1px solid #EEF1F6`} />)}
          </div>
        ) : null}
      </section>
    </div>
  );
}
